import { useMemo, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useDates } from "../../../../dates/DatesContext";
import { useFetch } from "../../../../hooks/api";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import DetailedMetricRow from "../../../core/blocks/DetailedMetricRow";
import PromotionsIcon from "../../../../assets/images/icons/promotion.svg";
import ThemeLine from "../ThemeLine";
import { keepPreviousData } from "@tanstack/react-query";
import { useAppSelector } from "../../../../redux/hooks";

interface PromotionsCardProps {
  category?: string | null;
}

const PromotionsCard = ({ category = null }: PromotionsCardProps): ReactElement => {
  const { t } = useTranslation(["merchandising"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { start, end, group, preStart, preEnd } = useDates();
  const filters = {
    market_place: marketPlace.marketPlace,
    group_by: group,
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
    pre_start: preStart.format("YYYY-MM-DD"),
    pre_end: preEnd.format("YYYY-MM-DD"),
    ...(category && { category }),
  };

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: promoReportData, isFetching } = useFetch<any>(
    ["promosReport", start, end, preStart, preEnd, group, category],
    `/merchandise/promos_report/`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const metrics = useMemo(() => {
    return [
      getMetricSpec("promo_sales"),
      getMetricSpec("promo_cost"),
      getMetricSpec("promo_units"),
      getMetricSpec("promo_conversion_rate"),
    ];
  }, []);

  return (
    <DetailedMetricRow
      title={t(($) => $.merchandising["Deals"])}
      to={`/user/merchandising/promotions`}
      byline={
        <>
          <ThemeLine>{t(($) => $.merchandising["Promotion"])}</ThemeLine>
          {t(($) => $.merchandising["Measure the impact of your deals"])}
          {isFetching && <i className="fa fa-spinner fa-spin"></i>}
        </>
      }
      totals={{ data: promoReportData?.total, loading: isFetching }}
      timeline={{ data: promoReportData?.dates, loading: isFetching }}
      metrics={metrics}
      icon={PromotionsIcon}
    />
  );
};

export default PromotionsCard;
